import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import StatsCard from '../../components/StatsCard'
import { Users, PawPrint, CreditCard, TrendingUp, MessageSquare, Zap } from 'lucide-react'
import {
  mockDashboardStats,
  mockAnalyticsData,
  mockRevenueByTier,
  mockPetDistribution,
} from '../../data/mockData'

const COLORS = ['#0d6e6e', '#f5a623', '#2ecb7a', '#e94b3c']

/**
 * Dashboard Page
 * Overview with KPI cards and performance charts
 */
export default function DashboardPage() {
  const stats = mockDashboardStats
  const revenueData = mockRevenueByTier
  const petData = mockPetDistribution

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Dashboard</h1>
        <p className="text-pawssible-slate-600 mt-1">Welcome to Pawssible Admin Panel</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          label="Total Users"
          value={stats.totalUsers.toLocaleString()}
          icon={Users}
          trend={stats.userGrowth}
          color="teal"
        />
        <StatsCard
          label="Active Pets"
          value={stats.totalPets.toLocaleString()}
          icon={PawPrint}
          trend={stats.petGrowth}
          color="amber"
        />
        <StatsCard
          label="Active Subscriptions"
          value={stats.activeSubscriptions}
          icon={CreditCard}
          color="teal"
        />
        <StatsCard
          label="Revenue (AED)"
          value={`${(stats.totalRevenue / 1000).toFixed(1)}k`}
          icon={TrendingUp}
          trend={stats.revenueGrowth}
          color="amber"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">User Growth (30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mockAnalyticsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }} />
              <Legend />
              <Line
                type="monotone"
                dataKey="users"
                stroke="#0d6e6e"
                dot={{ fill: '#0d6e6e', r: 5 }}
                activeDot={{ r: 7 }}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trend */}
        <div className="card">
          <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Revenue Trend (30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mockAnalyticsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }} />
              <Legend />
              <Bar dataKey="revenue" fill="#f5a623" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Tier */}
        <div className="card">
          <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Revenue by Tier</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }} />
              <Legend />
              <Bar dataKey="value" fill="#0d6e6e" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pet Type Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Pet Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={petData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {petData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => value.toLocaleString()} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Activity Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-pawssible-teal-100 rounded-lg">
            <Zap className="text-pawssible-teal-600" size={28} />
          </div>
          <div>
            <p className="text-sm text-pawssible-slate-600">AI Queries Today</p>
            <p className="text-2xl font-bold text-pawssible-slate-900">{stats.aiQueriestoday.toLocaleString()}</p>
          </div>
        </div>

        <div className="card flex items-center gap-4">
          <div className="p-3 bg-pawssible-amber-100 rounded-lg">
            <MessageSquare className="text-pawssible-amber-600" size={28} />
          </div>
          <div>
            <p className="text-sm text-pawssible-slate-600">Reminders Sent</p>
            <p className="text-2xl font-bold text-pawssible-slate-900">{stats.remindersSent.toLocaleString()}</p>
          </div>
        </div>

        <div className="card flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <Users className="text-green-600" size={28} />
          </div>
          <div>
            <p className="text-sm text-pawssible-slate-600">New Signups Today</p>
            <p className="text-2xl font-bold text-pawssible-slate-900">{stats.newSignups}</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button className="btn-primary">Create User</button>
          <button className="btn-secondary">View Reports</button>
          <button className="btn-secondary">Export Data</button>
          <button className="btn-secondary">System Status</button>
        </div>
      </div>
    </div>
  )
}
